import type { SectionProps } from "../context";
import { Confetti, Sparkles } from "../decor";
import { Icon } from "../icons";
import { Container, CtaButton, Eyebrow, Marquee as UiMarquee, MultiLine, REVEAL, SectionShell, SectionTitle, ctaVisible, cx } from "../ui";

export function Cta({ node, ctx }: SectionProps<"cta">) {
  const c = node.content;
  // Without a working button (e.g. WhatsApp CTA but no number yet) live sites drop it and its spacing.
  const button = ctaVisible(ctx, c.button);
  if (node.variant === "split") {
    return (
      <SectionShell ctx={ctx} node={node}>
        <Container>
          <div className={cx("baze-reveal grid gap-8 border-y border-site-border py-10 @3xl:items-center @3xl:gap-12 @3xl:py-14", button && "@3xl:grid-cols-[1.5fr_auto]")}>
            <div className="max-w-2xl">
              <Eyebrow ctx={ctx} text={c.eyebrow} path="content.eyebrow" className="mb-3" />
              <SectionTitle ctx={ctx} node={node} text={c.title} className="text-balance" />
              <MultiLine ctx={ctx} text={c.description} path="content.description" className="mt-4 text-lg leading-relaxed text-site-muted" />
            </div>
            <CtaButton ctx={ctx} cta={c.button} path="content.button" className="px-8 text-base @3xl:justify-self-end" withArrow />
          </div>
        </Container>
      </SectionShell>
    );
  }
  if (node.variant === "celebration") {
    return (
      <SectionShell ctx={ctx} node={node}>
        <Container>
          <div className="baze-reveal-pop relative isolate overflow-hidden rounded-[calc(var(--site-radius-lg)*1.5)] bg-site-primary px-6 py-16 text-center text-site-on-primary shadow-site-lg @3xl:px-16 @3xl:py-24">
            <div aria-hidden="true" className="absolute inset-0 -z-10 bg-[radial-gradient(60%_60%_at_20%_0%,color-mix(in_srgb,var(--site-accent)_45%,transparent),transparent_70%),radial-gradient(50%_60%_at_100%_100%,color-mix(in_srgb,var(--site-secondary)_40%,transparent),transparent_70%)]" />
            <Confetti className="-z-10 [&_i]:bg-site-on-primary/70! [&_i:nth-child(3n)]:bg-site-accent! [&_i:nth-child(3n+1)]:bg-site-secondary!" />
            <Sparkles className="-z-10 [&_svg]:text-site-on-primary/80!" />
            <div className="mx-auto max-w-2xl">
              <span aria-hidden="true" className="baze-wiggle mx-auto mb-6 grid size-14 place-items-center rounded-full bg-site-on-primary/15 ring-1 ring-site-on-primary/25">
                <Icon name="party" className="size-7" />
              </span>
              <Eyebrow ctx={ctx} text={c.eyebrow} path="content.eyebrow" className="mb-4 text-site-on-primary/85!" />
              <SectionTitle ctx={ctx} node={node} text={c.title} className="text-balance text-site-on-primary @3xl:text-[3.25rem]" />
              <MultiLine ctx={ctx} text={c.description} path="content.description" className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-site-on-primary/85" />
              {button ? (
                <div className="mt-9 flex justify-center">
                  <span className="relative inline-flex">
                    <span aria-hidden="true" className="baze-ping absolute inset-0 rounded-site-button bg-site-on-primary/30 opacity-0" />
                    <CtaButton ctx={ctx} cta={c.button} path="content.button" style="inverse" className="relative px-9 text-base shadow-site-lg" withArrow />
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        </Container>
      </SectionShell>
    );
  }
  if (node.variant === "marquee") {
    // The readable run holds the real heading (wrapped in a Container while static); while motion runs it
    // scrolls in one line with aria-hidden copies, so the copies must use the same type size.
    const size = "text-[2.75rem]! leading-none! @3xl:text-7xl! @5xl:text-8xl!";
    const sep = <Icon name="sparkle" className="baze-marquee-live mx-6 size-[0.6em] shrink-0 text-site-primary @3xl:mx-10" />;
    return (
      <SectionShell ctx={ctx} node={node} className="baze-band relative isolate overflow-hidden">
        <Container>
          <Eyebrow ctx={ctx} text={c.eyebrow} path="content.eyebrow" className="baze-reveal mb-6" />
        </Container>
        <UiMarquee
          seconds={Math.min(70, Math.max(24, c.title.length * 0.7))}
          className="baze-reveal border-y border-site-border py-6 @3xl:py-10"
          repeat={
            <span className={cx("baze-heading flex items-center", size)}>
              {c.title}
              {sep}
            </span>
          }
        >
          <span className="baze-marquee-static mx-auto block w-full max-w-6xl px-5 @3xl:px-8">
            <SectionTitle ctx={ctx} node={node} text={c.title} className={cx("text-balance", size)} />
          </span>
          <span aria-hidden="true" className={cx("baze-heading flex items-center", size)}>
            {sep}
          </span>
        </UiMarquee>
        <Container>
          <div className={cx("baze-reveal mt-8 grid gap-6 @3xl:mt-10 @3xl:items-center", button && "@3xl:grid-cols-[1.4fr_auto]")}>
            <MultiLine ctx={ctx} text={c.description} path="content.description" className="max-w-xl text-lg leading-relaxed opacity-85" />
            <CtaButton ctx={ctx} cta={c.button} path="content.button" className="px-8 text-base @3xl:justify-self-end" withArrow />
          </div>
        </Container>
      </SectionShell>
    );
  }
  if (node.variant === "spotlight") {
    return (
      // baze-band: paints its own full-bleed background, so it keeps its padding next to same-tone sections.
      <SectionShell ctx={ctx} node={node} className="baze-band relative isolate overflow-hidden">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
          <div className="baze-pan absolute inset-0 bg-linear-120 from-site-primary/0 via-site-primary/12 to-site-secondary/20" />
          <div className="baze-grid-lines absolute inset-0 text-site-text [mask-image:radial-gradient(ellipse_at_center,black_10%,transparent_70%)]" />
          <div className="baze-float absolute top-1/2 left-1/2 size-[30rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-site-primary/15 blur-3xl" />
        </div>
        <Container size="narrow" className={cx(REVEAL, "text-center")}>
          <Eyebrow ctx={ctx} text={c.eyebrow} path="content.eyebrow" className="mb-4" />
          <SectionTitle ctx={ctx} node={node} text={c.title} className="text-balance @3xl:text-[3.25rem] @5xl:text-6xl" />
          <MultiLine ctx={ctx} text={c.description} path="content.description" className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-site-muted" />
          {button ? (
          <div className="mt-9 flex justify-center">
            <span className="relative inline-flex">
              <span aria-hidden="true" className="baze-ping absolute inset-0 rounded-site-button bg-site-primary/25 opacity-0" />
              <CtaButton ctx={ctx} cta={c.button} path="content.button" className="relative px-9 text-base shadow-site-lg" withArrow />
            </span>
          </div>
          ) : null}
        </Container>
      </SectionShell>
    );
  }
  return (
    <SectionShell ctx={ctx} node={node}>
      <Container>
        <div className="baze-reveal-pop relative isolate overflow-hidden rounded-site-lg bg-site-primary px-6 py-14 text-center text-site-on-primary shadow-site-lg @3xl:px-16 @3xl:py-20">
          <div aria-hidden="true" className="baze-float absolute -top-20 -right-16 -z-10 size-72 rounded-full bg-site-on-primary/10" />
          <div aria-hidden="true" className="absolute -bottom-24 -left-20 -z-10 size-80 rounded-full bg-site-on-primary/5" />
          <div className="mx-auto max-w-2xl">
            <Eyebrow ctx={ctx} text={c.eyebrow} path="content.eyebrow" className="mb-4 text-site-on-primary/80!" />
            <SectionTitle ctx={ctx} node={node} text={c.title} className="text-balance text-site-on-primary" />
            <MultiLine ctx={ctx} text={c.description} path="content.description" className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-site-on-primary/85" />
            {button ? (
              <div className="mt-9 flex justify-center">
                <CtaButton ctx={ctx} cta={c.button} path="content.button" style="inverse" className="px-8 text-base" withArrow />
              </div>
            ) : null}
          </div>
        </div>
      </Container>
    </SectionShell>
  );
}
